import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ensureEndsWith, isNullOrWhitespace, randomString, showErrorSwal } from '@asamco/utils';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import { AppSettings } from '../configuration/appsettings';
import { BackEndApiType } from '../configuration/backend-api-type';
import { AuthenticationProvider, AuthenticationResult, LoginCredentials, LogoffParameter, UserSession } from './authentication.interfaces';
import { AzureB2CErrorCodes } from './azure-b2c-error-codes.class';
import { OidcConfigSetupService } from './oidc-config-setup.service';
import { redirectDestinationName } from './auth.consts';

/**
 * Azure B2C authentication provider, authenticates
 * through a azure B2C authentication flow.
 */
@Injectable({
	providedIn: 'root',
	deps: [AppSettings, OidcSecurityService, HttpClient, OidcConfigSetupService],
})
export class AzureB2CAuthenticationProvider implements AuthenticationProvider {
	backendType: BackEndApiType = BackEndApiType.B2C;

	/** Boolean for cancelling auth flow is case of error */
	private authCancelled: boolean = false;

	/**
	 * Flag to indicate we are trying to redirect to the reset password page.
	 * Very much not ideal but it works around a race condition happening in the
	 * login componant that eventually calls the login function of this class
	 * right after we use window.location.replace in handleError.
	 * The former seems to override (and thus win) all the time, preventing users
	 * from using the password reset function.
	 */
	private redirectingToPasswordReset: boolean = false;


	private readonly HARD_LOGOFF_KEY = 'HARD_LOGOFF';
	private readonly HARD_LOGOFF_TEXT;
	private readonly HARD_LOGOFF_HINT;

	constructor(
		private app: AppSettings,
		private oidc: OidcSecurityService,
		private http: HttpClient,
		private oidcConfigSetupService: OidcConfigSetupService
	) {
		// TODO: Translation not working here for some reason?
		this.HARD_LOGOFF_TEXT = 'Sign out all third party accounts';
		this.HARD_LOGOFF_HINT = 'Sign out all third party accounts (like Office365)';
	}

	initialize() {
		// Azure B2C uses query parameters to return errors from their login
		// so use regex to extract the error code and handle it accordingly
		const url = window.location.href;
		const regexResult: RegExpMatchArray = url.match('error_description=([A-z0-9]*)%3');

		if (regexResult && regexResult.length > 1) {
			const errorCode = regexResult[1];
			this.handleError(errorCode);
		}
	}

	restorePreviousSession(storedSessionData: UserSession): Observable<AuthenticationResult> {
		return this.oidc.isAuthenticated$.pipe(
			take(1), // TODO: Why take 1
			switchMap(_ => this.oidc.getAccessToken('configId')),
			map(token => {
				storedSessionData.user = {
					databaseId: -1,
					email: '',
					hasLicensing: false,
					isSuperAdmin: false,
					serverId: -1,
					sessionId: null,
					userId: -1,
					userName: '',
					isLicenseValid: true,
					displayName: '',
				};

				const result: AuthenticationResult = {
					authProvider: this,
					databaseId: 0,
					user: storedSessionData.user,
					server: null,
					token: token,
					success: true,
				};

				return result;
			})
		);
	}

	/** Log in using the oidc client */
	login(loginCreds?: LoginCredentials): Observable<AuthenticationResult> {
		if (this.redirectingToPasswordReset) {
			return of(null);
		}

		// Used to be:
		// localStorage.setItem('app-url', window.location.href.split('/#!')[0]);
		// If the URL limit will be reached we can consider using localStorage again,
		// but storing the 'rd' (redirect destination) query param; not the window.location.href.split('/#!')[0]
		// this would keep it cleaner and not so sensitive to assumptions.

		const [, query] = window.location.href.split('?');
		const params = new HttpParams({ fromString: query });
		const state = {
			nonce: randomString(40),
			// rd=redirect destination
			appUrl: params.has(redirectDestinationName) ? params.get(redirectDestinationName) : undefined,
		};
		return this.oidc.setState(JSON.stringify(state)).pipe(
			switchMap(_ => {
				this.oidc.authorize('configId', { // TODO: Maybe only add when explicitely logging out?
				customParams: {
					prompt: 'select_account',
				},
			});
			return of(null);
			})
		);
	}

	/**
	 * Whether the stored session is still valid
	 * @param storedSessionData
	 */
	getIsAuthorized(storedSessionData: UserSession): Observable<boolean> {
		if (this.authCancelled) return of(false);

		return this.oidc.checkAuth(undefined, 'configId').pipe(
			switchMap(res => {
				// Get the refreshtoken
				return this.oidc.getRefreshToken('configId').pipe(
					switchMap(rt => {
						// If not authenticated but refresh token, try to recover the session
						if (!res.isAuthenticated && this.refreshTokenValid(rt)) {
							return this.tryRecover();
						}

						// Else return the authorization result
						return of(res.isAuthenticated);
					})
				);
			})
		);
	}

	/**
	 * Log off by calling the oidc logoff function.
	 * By calling this function with hard=true it will
	 * also end the sessions of the IDPs (i.e. Office 365).
	 */
	logoff(clickedKey?: string): Observable<any> {
		return this.oidc.logoff('configId').pipe(
			switchMap(_ => {
				if(clickedKey && clickedKey === this.HARD_LOGOFF_KEY) {
					return this.oidc.getEndSessionUrl(undefined, 'configId').pipe(switchMap(url => this.http.get(url)))
				}
				return of(true);
			})
		)
	}

	/** Gets the settings for logoff */
	public getLogoffParameters(): LogoffParameter[] {
		return [
			{
				key: this.HARD_LOGOFF_KEY,
				text: this.HARD_LOGOFF_TEXT,
				hint: this.HARD_LOGOFF_HINT,
			},
		];
	}

	/** Check if refresh token is valid */
	private refreshTokenValid(refreshToken: string): boolean {
		return !isNullOrWhitespace(refreshToken);
	}

	/**
	 * Try to recover the session with the refresh token.
	 * Call checkAuth instead of isAuthorized$ to trigger the silent renew
	 * polling.
	 **/
	private tryRecover(): Observable<boolean> {
		return this.oidc.forceRefreshSession(undefined, 'configId').pipe(
			catchError(error => {
				console.error('Error while refreshing token', error);
				return of(false);
			}),
			switchMap(_ => this.oidc.checkAuth(undefined, 'configId')),
			map(lr => lr.isAuthenticated)
		);
	}

	/** Handles errors returned by the B2C login flow */
	private handleError(errorCode: string) {
		switch (errorCode) {
			case AzureB2CErrorCodes.ForgotPassword:
				const hostUrl = ensureEndsWith(window.location.origin, '/');
				this.redirectingToPasswordReset = true;
				this.oidcConfigSetupService.getConfig().subscribe({
					next: cfg => {
						window.location.replace(
							`https://${cfg.tenant}.b2clogin.com/${cfg.tenant}.onmicrosoft.com/oauth2/v2.0/authorize?p=${this.app.settings.b2c.forgotPasswordPolicy}&client_id=${cfg.clientId}&nonce=defaultNonce&redirect_uri=${hostUrl}&scope=openid&response_type=id_token&prompt=login`
						);
					},
					error: showErrorSwal
				});

				break;
			case AzureB2CErrorCodes.CancelledUserCreation:
				this.authCancelled = true;
				break;
			default:
				console.error('Unknow error occured while signing in to Azure AD B2C', errorCode);
				this.authCancelled = true;
				break;
		}
	}
}
